import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export type PillVariant = 'light' | 'dark' | 'brand' | 'highlight' | 'outline-light' | 'outline-dark'

export type PillSize = 'sm' | 'md' | 'lg'

const variantClasses: Record<PillVariant, string> = {
    light: 'bg-white text-ink hover:bg-brand hover:text-white focus-visible:outline-white',
    dark: 'bg-ink text-white hover:bg-brand focus-visible:outline-ink',
    brand: 'bg-brand text-white hover:bg-ink focus-visible:outline-brand',
    highlight: 'bg-highlight text-highlight-foreground hover:bg-white hover:text-ink focus-visible:outline-highlight',
    'outline-light': 'border border-white/30 text-white hover:border-white hover:bg-white hover:text-ink focus-visible:outline-white',
    'outline-dark': 'border border-ink/20 text-ink hover:border-ink hover:bg-ink hover:text-white focus-visible:outline-ink'
}

const sizeClasses: Record<PillSize, string> = {
    sm: 'h-10 px-5 text-[11px]',
    md: 'h-11 px-6 text-xs',
    lg: 'h-14 px-8 text-[13px]'
}

export const pillClassName = (variant: PillVariant = 'dark', size: PillSize = 'md', className?: string): string => {
    return cn(
        'group inline-flex shrink-0 items-center justify-center gap-2.5 rounded-full font-mono uppercase tracking-[0.14em] whitespace-nowrap transition-colors duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-60',
        variantClasses[variant],
        sizeClasses[size],
        className
    )
}

export const PillArrow: FC = (): ReactNode => {
    return (
        <ArrowUpRight
            aria-hidden='true'
            className='size-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5'
        />
    )
}

interface PillLinkProps {
    href: string
    children: ReactNode
    variant?: PillVariant
    size?: PillSize
    className?: string
    arrow?: boolean
}

export const PillLink: FC<PillLinkProps> = ({
    href,
    children,
    variant = 'dark',
    size = 'md',
    className,
    arrow = true
}): ReactNode => {
    const classes = pillClassName(variant, size, className)
    const content = (
        <>
            {children}
            {arrow && <PillArrow />}
        </>
    )

    if (/^https?:/.test(href)) {
        return (
            <a href={href} target='_blank' rel='noopener noreferrer' className={classes}>
                {content}
            </a>
        )
    }

    if (/^(mailto|tel):/.test(href)) {
        return (
            <a href={href} className={classes}>
                {content}
            </a>
        )
    }

    return (
        <Link href={href} className={classes}>
            {content}
        </Link>
    )
}
